import { RolodexIndication } from 'features/indicationTriage'
import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { parseRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import { useMemo } from 'react'

/**
 * An LTA indication paired with its parsed region classification.
 *
 * This pairing exists so the transport blob is parsed ONCE per data load.
 * `parseRegionClassification` runs a Zod parse plus the full cross-field domain
 * validation (which re-runs the component classifier), and it used to be called
 * independently by the pre-planning step's list rows, its aggregate, the
 * classification form, the part-step validator, and the part step's detail-card
 * config — five places deriving the same value, one of them inside a render loop.
 */
export interface ClassifiedLta {
  indication: RolodexIndication
  /**
   * `null` when absent, malformed, or domain-inconsistent. Consumers treat null
   * permissively (Pre-Planning is skippable): the LTA is still runnable, just
   * without a validated route.
   */
  classification: FfsRegionClassification | null
}

/**
 * Pair every LTA with its parsed classification. Pure, so the part-step and
 * pre-planning validators can be unit-tested without React.
 */
export function classifyLtas(
  indications: ReadonlyArray<RolodexIndication>
): ReadonlyArray<ClassifiedLta> {
  return indications.map((indication) => ({
    indication,
    classification: parseRegionClassification(
      indication.customInfo?.ffsRegionClassification
    ),
  }))
}

/** `classifyLtas`, memoized on the indication list. */
export function useClassifiedLtas(
  indications: ReadonlyArray<RolodexIndication>
): ReadonlyArray<ClassifiedLta> {
  return useMemo(() => classifyLtas(indications), [indications])
}
